/* =========================================================================
   REEKON — Price + Shop Pay installments card
   - Tier-aware copy: monthly installments at >= $150, 4 payments under that.
   - Updates label, amount, and "/mo" suffix when the customer changes
     the selected variant (e.g. blade type).
   - The visible "View Sample Plans" link sits underneath a transparent
     <form data-payment-terms-target> that holds the live
     <shopify-payment-terms> element. Mouse clicks pass straight through
     to the component, so Shopify's own modal opens natively.
   - Keyboard activations (Tab → Enter on the link) are handled here as a
     fallback by dispatching a real MouseEvent on the component.

   This file is safe to load on every page; it's a no-op when no
   [data-reekon-price-row] elements are present.
   ========================================================================= */
(function () {
  'use strict';

  function moneyFormat(cents) {
    var dollars = cents / 100;
    if (dollars === Math.floor(dollars)) return '$' + dollars.toFixed(0);
    return '$' + dollars.toFixed(2);
  }

  function readNumberAttr(el, attr, fallback) {
    var v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? fallback : v;
  }

  /* ---------- Variant lookup --------------------------------------------
   * Try Empire's <script data-section-data> first (product is nested at
   * .product), then fall back to vintage-theme product JSON locations.
   * Returns variant price in cents, or null if not found.
   * --------------------------------------------------------------------- */
  function findVariantPriceCents(productId, variantId) {
    // Empire (and most modern OS-2.0 themes): section JSON nests product.
    var sectionNodes = document.querySelectorAll('script[data-section-data], script[data-section-type][type="application/json"]');
    for (var i = 0; i < sectionNodes.length; i++) {
      try {
        var data = JSON.parse(sectionNodes[i].textContent);
        var p = data && data.product;
        if (!p || String(p.id) !== String(productId)) continue;
        var variants = p.variants || [];
        for (var j = 0; j < variants.length; j++) {
          if (String(variants[j].id) === String(variantId)) return variants[j].price;
        }
      } catch (e) {}
    }

    // Vintage-theme fallback: standalone product JSON script.
    var candidates = [
      'script#ProductJson-' + productId,
      'script[data-product-json][data-product-id="' + productId + '"]',
      'script[data-product-json]'
    ];
    for (var k = 0; k < candidates.length; k++) {
      var node = null;
      try { node = document.querySelector(candidates[k]); } catch (e) {}
      if (!node) continue;
      try {
        var pdata = JSON.parse(node.textContent);
        var pvariants = pdata.variants || [];
        for (var l = 0; l < pvariants.length; l++) {
          if (String(pvariants[l].id) === String(variantId)) return pvariants[l].price;
        }
      } catch (e) {}
    }

    return null;
  }

  function getCurrentVariantId() {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    var input = form.querySelector('[name="id"]');
    return input ? input.value : null;
  }

  /* ---------- Tier-aware copy ------------------------------------------- */
  function applyTier(root, priceCents) {
    var threshold = readNumberAttr(root, 'data-reekon-tier-threshold', 100000);
    var months = readNumberAttr(root, 'data-reekon-monthly-months', 12);
    var splits = readNumberAttr(root, 'data-reekon-installment-count', 4);

    var labelEl = root.querySelector('[data-reekon-label]');
    var amountEl = root.querySelector('[data-reekon-amount]');
    var suffixEl = root.querySelector('[data-reekon-suffix]');

    // Shop Pay rounds each scheduled payment UP to the nearest cent so
    // the sum is guaranteed >= total. Use Math.ceil to match.
    var tier, perPaymentCents, label, suffix;
    if (priceCents >= threshold) {
      tier = 'monthly';
      perPaymentCents = Math.ceil(priceCents / months);
      label = 'Starting at';
      suffix = '/mo';
    } else {
      tier = 'split';
      perPaymentCents = Math.ceil(priceCents / splits);
      label = splits + ' payments of';
      suffix = '';
    }

    root.setAttribute('data-reekon-tier', tier);
    if (labelEl) labelEl.textContent = label;
    if (amountEl) amountEl.textContent = moneyFormat(perPaymentCents);
    if (suffixEl) suffixEl.textContent = suffix;
  }

  function updateForVariant(root) {
    var variantId = getCurrentVariantId();
    if (!variantId) return;
    var productId = root.getAttribute('data-reekon-product-id');
    var cents = findVariantPriceCents(productId, variantId);
    if (cents == null) return;
    applyTier(root, cents);
    root.setAttribute('data-reekon-variant-id', variantId);
  }

  /* ---------- Click forwarding ----------------------------------------
   * <shopify-payment-terms> on Shopify's modern stack is a *data-carrier*
   * web component: it has shopify-meta + variant-id attributes and is
   * registered via data-instance-id, but it typically renders no visible
   * light-DOM children. Two places its click-to-open-modal handler can
   * actually live:
   *
   *   (a) Inside its shadow root, on a banner anchor / button.
   *   (b) On the component element itself, registered by Shopify's
   *       installments banner manager and listening for clicks on the
   *       host element.
   *
   * We try (a) first, then (b). Both use real MouseEvents dispatched
   * synchronously inside our click handler so the user-activation
   * token is preserved (popup blockers + trusted-event gates).
   *
   * NOTE: temporary diagnostic logs are gated behind window.REEKON_DEBUG.
   * Set window.REEKON_DEBUG = true in your console to see what trigger
   * was selected; remove it (or remove the gates) once we're confident.
   * --------------------------------------------------------------------- */
  function rDebug() {
    if (window.REEKON_DEBUG) {
      try { console.log.apply(console, ['[reekon-shoppay]'].concat([].slice.call(arguments))); } catch (e) {}
    }
  }

  function dispatchRealClick(target) {
    if (!target) return false;
    var ev = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
      button: 0,
      composed: true
    });
    target.dispatchEvent(ev);
    return true;
  }

  function findModalTrigger() {
    var component = document.querySelector('shopify-payment-terms');
    if (!component) {
      rDebug('No <shopify-payment-terms> element on page');
      return null;
    }

    // (a) Shadow DOM — likely on modern Shopify.
    if (component.shadowRoot) {
      rDebug('Component has shadowRoot, searching inside');
      var shadowTrigger = (
        component.shadowRoot.querySelector('a[href*="installments"]') ||
        component.shadowRoot.querySelector('a[href*="shop.app"]') ||
        component.shadowRoot.querySelector('button[aria-haspopup]') ||
        component.shadowRoot.querySelector('button') ||
        component.shadowRoot.querySelector('a') ||
        component.shadowRoot.querySelector('[role="button"]')
      );
      if (shadowTrigger) {
        rDebug('Found shadow trigger:', shadowTrigger);
        return shadowTrigger;
      }
      rDebug('No clickable inside shadowRoot; will fall back to host element');
    } else {
      rDebug('Component has no shadowRoot (or it is closed)');
    }

    // (b) Light DOM — older Shopify renderings.
    var lightTrigger = component.querySelector(
      'a[href*="installments"], a[href*="shop.app"], button, a, [role="button"]'
    );
    if (lightTrigger) {
      rDebug('Found light DOM trigger:', lightTrigger);
      return lightTrigger;
    }

    // (c) Host element itself — installments banner manager listens here.
    rDebug('Falling back to clicking the host <shopify-payment-terms> element');
    return component;
  }

  function forwardClick(e) {
    if (e && e.preventDefault) e.preventDefault();
    var trigger = findModalTrigger();
    if (trigger) {
      dispatchRealClick(trigger);
      return;
    }
    rDebug('No trigger found anywhere; opening shop.app in new tab');
    window.open('https://shop.app/pay-installments', '_blank', 'noopener');
  }

  /* ---------- Init / refresh ------------------------------------------- */
  function bindRoot(root) {
    if (root.__reekonInit) return;
    root.__reekonInit = true;
    var link = root.querySelector('[data-reekon-prequal]');
    if (link) link.addEventListener('click', forwardClick);
    updateForVariant(root);
  }

  function refreshAll() {
    var roots = document.querySelectorAll('[data-reekon-price-row]');
    Array.prototype.forEach.call(roots, updateForVariant);
  }

  function init() {
    var roots = document.querySelectorAll('[data-reekon-price-row]');
    Array.prototype.forEach.call(roots, bindRoot);
  }

  /* ---------- Variant change wiring ------------------------------------- */
  document.addEventListener('change', function (e) {
    var t = e && e.target;
    if (t && t.matches && t.matches('[name="id"]')) refreshAll();
  });
  document.addEventListener('variant:change', refreshAll);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
